import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { TweaksPanel, TweakSection, TweakText, TweakRadio } from './tweaks-panel';

// Sentinel id used by URL-param "preview" mode (no real agent fetched).
// Used downstream to skip the chat-widget injection and the chat callout arrow.
const SYNTHETIC_AGENT_ID = '__preview_demo__';

/**
 * AgentDemoPage — redesigned for cold-traffic conversion.
 *
 * Schools-first, but works generically. Keeps all original API plumbing:
 *   - fetch agent by slug
 *   - fetch template demo_page (non-school)
 *   - fetch live analytics + recent_leads
 *   - fetch RAG scraped sources
 *   - inject the production chat widget script
 *
 * Design notes (vs old version):
 *   - Dark navy hero with mesh dots (matches the schools flyer for visual continuity)
 *   - Data-driven headline (5× / 98% / 0)
 *   - QR is the final conversion action after the page has made the case
 *   - Phone-style chat mock from the flyer, embedded in hero on mobile
 *   - Channels row from the flyer ("one assistant — every channel")
 *   - Removed: yellow trial banner, "Why Choose" wall, duplicate Contact blocks,
 *     emoji-heavy feature cards, pricing
 */

const SALES_PHONE = '+918555811800';
const SALES_PHONE_PRETTY = '+91 85558 11800';
const SALES_EMAIL = 'sales@serviti.ai';
const WA_LINK = `https://wa.me/${SALES_PHONE.replace(/[^0-9]/g, '')}?text=Hi%2C%20I%27d%20like%20a%20demo%20for%20Serviti%20AI`;

const AgentDemoPage = () => {
  const { agentName } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [agent, setAgent] = useState(null);

  // Sales-only edit mode: ?edit=1 reveals the Tweaks panel.
  // Tweaks write back to URL so sales can copy the personalized URL and share.
  const isEdit = searchParams.get('edit') === '1';
  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value.trim()) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const clearParam = (key) => {
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const [demoPage, setDemoPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liveStats, setLiveStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [scrapedSources, setScrapedSources] = useState(null);

  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const apiUrl = `${baseUrl}/api/v1`;

  // ---------- Load agent ----------
  useEffect(() => {
    const loadAgent = async () => {
      // ----- URL-param "preview" mode (no slug — synthetic agent from query string) -----
      // Lets sales send shareable links like:
      //   /preview?school=Greenwood%20International%20School
      //   /preview?business=Acme%20Cafe
      //   /preview                         (generic cold-campaign default)
      // No DB fetch, no widget injection, no agent record needed.
      if (!agentName) {
        const schoolParam = searchParams.get('school');
        const businessParam = searchParams.get('business');
        const nameParam = searchParams.get('name');
        const typeParam = searchParams.get('type'); // 'school' | 'business' | undefined
        const tenantName = schoolParam || businessParam || nameParam || '';
        const isSchoolMode = typeParam === 'school' || (!!schoolParam && !typeParam);
        setAgent({
          id: SYNTHETIC_AGENT_ID,
          name: tenantName || (isSchoolMode ? 'Your School' : 'Your Business'),
          tenant_name: tenantName || null,
          prompt_template_name: isSchoolMode ? 'school_website_assistant.json' : null,
          welcome_message: null,
          rag_collection: null,
        });
        setError(null);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const slug = agentName.replace(/\s+/g, '-').replace(/[^\w-]/g, '');
        const response = await fetch(`${apiUrl}/agents/by-slug/${encodeURIComponent(slug)}`);
        if (!response.ok) throw new Error('Agent not found');
        const data = await response.json();
        setAgent(data);

        if (data.prompt_template_name && data.prompt_template_name !== 'school_website_assistant.json') {
          try {
            const templateRes = await fetch(`${apiUrl}/agents/system-prompts/${encodeURIComponent(data.prompt_template_name)}`);
            if (templateRes.ok) {
              const templateData = await templateRes.json();
              if (templateData.demo_page) setDemoPage(templateData.demo_page);
            }
          } catch (e) { console.warn('Could not fetch template demo_page:', e); }
        }
        setError(null);

        // Live analytics
        fetch(`${apiUrl}/analytics/public/${data.id}`)
          .then(r => r.json())
          .then(d => {
            if (d.success && d.stats?.total_conversations > 0) {
              setLiveStats(d.stats);
              if (d.recent_leads?.length > 0) setRecentLeads(d.recent_leads.slice(0, 4));
            }
          })
          .catch(() => {});
      } catch (err) {
        console.error('Error loading agent:', err);
        setError('Agent not found or unavailable');
      } finally {
        setLoading(false);
      }
    };
    loadAgent();
  }, [agentName, apiUrl, searchParams]);

  // ---------- Scraped sources ----------
  useEffect(() => {
    if (!agent?.rag_collection) return;
    if (agent?.id === SYNTHETIC_AGENT_ID) return;
    const fetchSources = async () => {
      try {
        const colRes = await fetch(`${apiUrl}/rag/collections`);
        const colData = await colRes.json();
        const collection = colData.collections?.find(c => c.name === agent.rag_collection);
        if (!collection) return;
        const srcRes = await fetch(`${apiUrl}/rag/collections/${collection.id}/sources`);
        const srcData = await srcRes.json();
        setScrapedSources(srcData);
      } catch (e) { console.warn('Failed to load sources:', e); }
    };
    fetchSources();
  }, [agent, apiUrl]);

  // ---------- Inject widget ----------
  useEffect(() => {
    if (!agent) return;
    // Synthetic preview agents have no real backend chat — skip widget.
    if (agent.id === SYNTHETIC_AGENT_ID) return;
    const existingContainer = document.getElementById('serviti-chat-container');
    if (existingContainer) existingContainer.remove();
    const existingScript = document.getElementById('serviti-widget-script');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.id = 'serviti-widget-script';
    script.src = '/embed/serviti-chat-widget.js';
    // Only inject data-api-url in local dev — no deployed config exists then.
    // In built environments the widget self-resolves from serviti-widget-config.json
    // served beside it, so the demo exercises the decoupled path like a real embed.
    if (process.env.NODE_ENV === 'development') {
      script.setAttribute('data-api-url', apiUrl);
    }
    script.setAttribute('data-agent-id', agent.id);
    script.setAttribute('data-position', 'bottom-right');
    script.setAttribute('data-theme', 'light');
    script.setAttribute('data-auto-open', 'true');
    document.body.appendChild(script);

    return () => {
      const container = document.getElementById('serviti-chat-container');
      if (container) container.remove();
      const widgetScript = document.getElementById('serviti-widget-script');
      if (widgetScript) widgetScript.remove();
    };
  }, [agent, apiUrl]);

  if (loading) {
    return (
      <div style={loadingWrapStyle}>
        <div style={{ textAlign: 'center' }}>
          <div style={spinnerStyle}></div>
          <p style={{ marginTop: 16, color: '#6b7280' }}>Loading demo…</p>
        </div>
        <style>{spinnerKeyframes}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={loadingWrapStyle}>
        <div style={{ textAlign: 'center', maxWidth: 420 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🤖</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1f2937', margin: '0 0 8px' }}>Demo Unavailable</h2>
          <p style={{ color: '#6b7280', margin: 0 }}>{error}</p>
          <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 16 }}>
            The agent "{agentName}" could not be found. Please check the URL and try again.
          </p>
        </div>
      </div>
    );
  }

  // ---------- Derive content ----------
  const isSchool = agent.prompt_template_name === 'school_website_assistant.json';
  const tenantName = (isSchool ? (agent.tenant_name || agent.name) : agent.name) || 'Your Business';
  const shortName = (tenantName.split(/\s+/)[0] || 'Your').slice(0, 20);
  const audienceWord = isSchool ? 'parent' : 'customer';
  const audienceWordCap = isSchool ? 'Parent' : 'Customer';
  const lossWord = isSchool ? 'enrollment' : 'customer';

  const heroTitle = isSchool
    ? <>Stop losing admissions to a <span style={{ color: '#93c5fd' }}>"Contact Us" form.</span></>
    : demoPage?.hero_title
      ? <>{demoPage.hero_title}</>
      : <>Turn every {audienceWord} inquiry into a <span style={{ color: '#93c5fd' }}>captured lead.</span></>;

  const heroSub = isSchool
    ? <>An AI assistant trained on <b style={{ color: '#fff' }}>{tenantName}'s</b> programs, fees, curriculum &amp; admissions — answering parents in &lt;3 seconds, capturing every lead, 24/7.</>
    : demoPage?.hero_subtitle
      ? demoPage.hero_subtitle
      : <>An AI assistant trained on <b style={{ color: '#fff' }}>{tenantName}'s</b> products, services &amp; FAQs — answering visitors in &lt;3 seconds, capturing every lead, 24/7.</>;

  // Stats (live or fallback)
  const stats = liveStats ? {
    convos: liveStats.total_conversations,
    answered: liveStats.questions_answered_pct || 98,
    leads: liveStats.total_leads,
    response: '<3s',
  } : { convos: '500+', answered: 98, leads: '40+', response: '<3s' };

  // Leads (live or fallback)
  const fallbackLeads = isSchool ? [
    { name: 'Priya Sharma', phone: '+91 98765 4XXXX', query: 'Grade 6 fees and CBSE vs IB curriculum?', status: 'qualified', priority: 'hot', date: 'Today' },
    { name: 'Rahul Mehta', phone: '+91 91234 5XXXX', query: 'Admissions cutoff for 2026-27 — can I still apply?', status: 'new', priority: 'warm', date: 'Today' },
    { name: 'Anjali Reddy', phone: '+91 99887 6XXXX', query: 'Scholarship eligibility for Grade 11 STEM?', status: 'qualified', priority: 'hot', date: 'Yesterday' },
    { name: 'Vikram Iyer', phone: '+91 90123 4XXXX', query: 'Bus route for HSR Layout area?', status: 'new', priority: 'warm', date: 'Yesterday' },
  ] : [
    { name: 'Priya Sharma', phone: '+91 98765 4XXXX', query: 'Pricing for the premium package?', status: 'qualified', priority: 'hot', date: 'Today' },
    { name: 'Rahul Mehta', phone: '+91 91234 5XXXX', query: 'Availability next week — can I book a slot?', status: 'new', priority: 'warm', date: 'Today' },
  ];
  const leads = (recentLeads.length > 0 ? recentLeads : (demoPage?.sample_leads?.length ? demoPage.sample_leads : fallbackLeads)).slice(0, 4);

  return (
    <div style={pageStyle}>
      <style>{globalStyle}</style>

      {/* Top bar */}
      <header style={topbarStyle}>
        <div style={brandStyle}>
          <img src="/serviti_logo_dark.png" alt="Serviti AI" style={{ height: 28 }} />
          <div style={brandByStyle}>
            <b>{tenantName}</b>
            <span>Powered by Serviti AI</span>
          </div>
        </div>
        <div style={topbarQuickStyle}>
          <span>Already a customer?</span>
          <a href="https://app.serviti.ai" target="_blank" rel="noopener noreferrer" style={topbarLinkStyle}>Sign in →</a>
        </div>
      </header>

      <main style={mainStyle}>

        {/* HERO */}
        <section style={heroStyle}>
          <div style={heroContentStyle}>
            <span style={heroPillStyle}>
              <span style={heroPillDotStyle}></span>
              {isSchool ? '30-day Growth trial · Limited launch offer' : 'Start a 30-day Growth trial'}
            </span>
            <div style={heroEyebrowStyle}>
              For {isSchool ? 'school admissions teams' : 'sales & customer teams'}
            </div>
            <h1 style={heroTitleStyle}>{heroTitle}</h1>
            <p style={heroSubStyle}>{heroSub} Live on your website in hours.</p>

            <div className="serviti-hero-metrics">
              <div style={heroMetricStyle}>
                <div style={heroMetricNumStyle}>5<small style={{ fontSize: 16, fontWeight: 700 }}>×</small></div>
                <div style={heroMetricLblStyle}>More leads captured</div>
              </div>
              <div style={heroMetricStyle}>
                <div style={heroMetricNumStyle}>{stats.answered}<small style={{ fontSize: 16, fontWeight: 700 }}>%</small></div>
                <div style={heroMetricLblStyle}>Auto-answered</div>
              </div>
              <div style={heroMetricStyle}>
                <div style={heroMetricNumStyle}>0</div>
                <div style={heroMetricLblStyle}>Missed inquiries</div>
              </div>
            </div>
          </div>

          {/* Phone-style mock from the flyer — visible only when widget is hidden */}
          <div className="serviti-phone-mock">
            <PhoneMock tenantName={tenantName} shortName={shortName} isSchool={isSchool} />
          </div>
        </section>

        {/* Mock chat widget — renders for synthetic agents (no real backend chat). Auto-plays a scripted demo. */}
        {agent.id === SYNTHETIC_AGENT_ID && (
          <MockChatWidget tenantName={tenantName} shortName={shortName} isSchool={isSchool} />
        )}

        {/* HOW IT WORKS */}
        <Section eyebrow="How it works" title={`From ${audienceWord} question to lead in 4 seconds.`}
                 sub="No forms. No waiting. No staff intervention. Every inquiry tracked, scored, and routed automatically.">
          <div className="serviti-how">
            {[
              { n: 1, h: `${audienceWordCap} starts chatting`, p: `On your ${isSchool ? 'school' : ''} website, or by scanning a QR on a flyer, hoarding, or social ad.` },
              { n: 2, h: 'AI answers instantly', p: isSchool ? 'Programs, fees, deadlines, eligibility, scholarships — answered in <3 seconds.' : 'Pricing, availability, services, FAQs — answered in <3 seconds.' },
              { n: 3, h: 'Lead captured', p: `${audienceWordCap} contact details + full inquiry context saved automatically.` },
              { n: 4, h: 'Team alerted', p: `${isSchool ? 'Admissions team' : 'Your team'} gets WhatsApp + email instantly. Move from new → contacted → ${isSchool ? 'enrolled' : 'converted'}.` },
            ].map(s => (
              <div key={s.n} style={stepStyle}>
                <div style={stepNumStyle}>{s.n}</div>
                <h4 style={stepHStyle}>{s.h}</h4>
                <p style={stepPStyle}>{s.p}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* CHANNELS — from the flyer */}
        <Section eyebrow="Multi-channel reach" title={`One AI assistant — every channel ${audienceWord}s use.`}
                 sub="Print a QR on flyers and hoardings, drop a link in WhatsApp campaigns, or embed a chat widget on your website. Same assistant, same lead pipeline.">
          <div className="serviti-channels">
            <ChannelCard color="#ea580c" bg="#fff7ed" title="Print & hoardings" desc="QR on flyers, brochures, gate banners">
              <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="10" y="6" width="44" height="52" rx="3"/><line x1="16" y1="14" x2="38" y2="14"/><line x1="16" y1="20" x2="34" y2="20"/>
                <rect x="34" y="30" width="16" height="16" rx="1"/><rect x="36" y="32" width="3" height="3" fill="currentColor" stroke="none"/>
                <rect x="45" y="32" width="3" height="3" fill="currentColor" stroke="none"/><rect x="36" y="41" width="3" height="3" fill="currentColor" stroke="none"/>
                <rect x="45" y="42" width="3" height="3" fill="currentColor" stroke="none"/>
              </svg>
            </ChannelCard>
            <ChannelCard color="#25d366" bg="#dcfce7" title="WhatsApp link" desc="Drop in promos, broadcasts, replies">
              <svg viewBox="0 0 64 64" fill="currentColor"><path d="M32 4C16.5 4 4 16.5 4 32c0 5 1.4 9.8 4 14L4 60l14.4-3.8c4 2.2 8.7 3.5 13.6 3.5C47.5 59.7 60 47.2 60 31.7 60 16.5 47.5 4 32 4zm13.4 33.3c-.7-.4-4.3-2.1-5-2.4-.7-.2-1.2-.4-1.7.4-.5.7-1.9 2.4-2.4 2.9-.4.5-.9.5-1.6.2-.7-.4-3.1-1.1-5.9-3.6-2.2-1.9-3.6-4.3-4.1-5-.4-.7 0-1.1.3-1.5.3-.3.7-.9 1.1-1.3.4-.4.5-.7.7-1.2.2-.5.1-.9-.1-1.3-.2-.4-1.7-4-2.3-5.5-.6-1.5-1.2-1.3-1.7-1.3h-1.4c-.5 0-1.3.2-2 .9s-2.6 2.5-2.6 6.2c0 3.7 2.7 7.2 3 7.7.4.5 5.3 8.1 12.9 11.4 1.8.8 3.2 1.2 4.3 1.6 1.8.6 3.5.5 4.8.3 1.5-.2 4.3-1.8 5-3.5.6-1.7.6-3.2.4-3.5-.1-.3-.6-.5-1.3-.9z"/></svg>
            </ChannelCard>
            <ChannelCard color="#2563eb" bg="#dbeafe" title="Website widget" desc="Embed chat bubble on any page">
              <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="6" y="10" width="52" height="36" rx="3"/><line x1="6" y1="18" x2="58" y2="18"/>
                <circle cx="11" cy="14" r="1" fill="currentColor"/><circle cx="15" cy="14" r="1" fill="currentColor"/><circle cx="19" cy="14" r="1" fill="currentColor"/>
                <circle cx="50" cy="40" r="10" fill="currentColor" stroke="none" opacity="0.85"/>
                <path d="M46 38h8M46 41h6" stroke="#fff" strokeWidth="1.5"/>
              </svg>
            </ChannelCard>
            <ChannelCard color="#bc1888" bg="transparent" title="Social ads" desc="Link in bio, Insta/FB CTAs, story stickers">
              <svg viewBox="0 0 64 64" fill="none">
                <defs>
                  <linearGradient id="ig-grad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#fdc468"/><stop offset="30%" stopColor="#f76b1c"/>
                    <stop offset="60%" stopColor="#dc2743"/><stop offset="85%" stopColor="#cc2366"/><stop offset="100%" stopColor="#bc1888"/>
                  </linearGradient>
                </defs>
                <rect x="6" y="6" width="52" height="52" rx="13" fill="url(#ig-grad)"/>
                <rect x="15" y="15" width="34" height="34" rx="10" fill="none" stroke="#fff" strokeWidth="3"/>
                <circle cx="32" cy="32" r="8" fill="none" stroke="#fff" strokeWidth="3"/>
                <circle cx="43" cy="21" r="2.2" fill="#fff"/>
              </svg>
            </ChannelCard>
          </div>
          <div style={flowRowStyle}>
            <span><b style={{ color: '#1d4ed8' }}>{audienceWordCap} engages</b></span>
            <span style={flowArrowStyle}>→</span>
            <span>AI answers instantly</span>
            <span style={flowArrowStyle}>→</span>
            <span>Lead captured</span>
            <span style={flowArrowStyle}>→</span>
            <span><b style={{ color: '#1d4ed8' }}>Team alerted on WhatsApp + email</b></span>
          </div>
        </Section>

        {/* DASHBOARD */}
        <Section eyebrow="Live dashboard" title="Every conversation becomes a tracked lead."
                 sub="Hot leads flagged automatically. Filter, qualify, follow up — all in one place. Your pipeline, finally visible.">
          <div style={dashCardStyle}>
            <div style={dashHeadStyle}>
              <div style={{ display: 'flex', gap: 5 }}>
                <i style={dashDotStyle}></i><i style={dashDotStyle}></i><i style={dashDotStyle}></i>
              </div>
              <span style={dashUrlStyle}>app.serviti.ai/leads</span>
              <span style={dashLiveStyle}><i style={dashLiveDotStyle}></i> Live</span>
            </div>
            <div className="serviti-dash-stats">
              <DashStat color="#4ade80" num={stats.convos} lbl="Conversations / month" />
              <DashStat color="#c4b5fd" num={`${stats.answered}%`} lbl="Auto-answered" />
              <DashStat color="#93c5fd" num={stats.leads} lbl="Leads / month" />
              <DashStat color="#fcd34d" num={stats.response} lbl="Response time" />
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={leadsTableStyle}>
                <thead>
                  <tr>
                    <th style={leadsThStyle}>{audienceWordCap}</th>
                    <th style={leadsThStyle}>Inquiry</th>
                    <th style={leadsThStyle}>Status</th>
                    <th style={leadsThStyle}>Priority</th>
                    <th style={leadsThStyle}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l, i) => (
                    <tr key={i} style={i < leads.length - 1 ? { borderBottom: '1px solid #f3f4f6' } : {}}>
                      <td style={leadsTdStyle}>
                        <div style={{ fontWeight: 600, color: '#111827' }}>{l.name}</div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{l.phone}</div>
                      </td>
                      <td style={{ ...leadsTdStyle, color: '#374151', maxWidth: 280 }}>{l.query}</td>
                      <td style={leadsTdStyle}><Badge kind={l.status === 'qualified' ? 'qual' : l.status === 'new' ? 'new' : 'cold'}>{cap(l.status)}</Badge></td>
                      <td style={leadsTdStyle}><Badge kind={l.priority || 'warm'}>{cap(l.priority || 'warm')}</Badge></td>
                      <td style={{ ...leadsTdStyle, color: '#6b7280', fontSize: 12 }}>{l.date || 'Today'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Section>

        {/* ALERTS */}
        <Section eyebrow="Real-time alerts" title="Your team knows the moment a lead shows interest."
                 sub="No more checking dashboards. Every captured lead pings WhatsApp and email instantly — with full context.">
          <div className="serviti-alerts">
            <WhatsAppMock tenantName={tenantName} isSchool={isSchool} />
            <EmailMock tenantName={tenantName} shortName={shortName} isSchool={isSchool} />
          </div>
        </Section>

        {/* SETUP */}
        <Section eyebrow="Setup" title="From sign-up to live assistant — same day."
                 sub="We do the heavy lifting. Your team focuses on what they do best — talking to qualified leads.">
          <div style={setupStyle}>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                `Auto-imports content from your existing ${isSchool ? 'school ' : ''}website`,
                `Upload ${isSchool ? 'prospectus, fee structure, brochures' : 'PDFs, FAQs, product docs'}`,
                'No code, no plugins, no IT involvement',
                'One snippet on your website — done',
                'Custom-branded with your logo and colors',
                'WhatsApp + email recipients configurable per team',
              ].map((t, i) => (
                <li key={i} style={setupLiStyle}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18, flexShrink: 0, marginTop: 2 }}>
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  {t}
                </li>
              ))}
            </ul>
            <div style={timingStyle}>
              <div style={timingBigStyle}>&lt; 1 day</div>
              <div style={timingLblStyle}>From demo call to live assistant on your site</div>
              <div style={timingNoteStyle}>
                We'll scrape your website, train the assistant on your {isSchool ? 'admissions' : ''} content, and embed the chat widget — typically the same business day.
              </div>
            </div>
          </div>
        </Section>

        {/* FINAL CTA */}
        <section style={finalStyle}>
          <div>
            <h3 style={finalTitleStyle}>
              Every unanswered inquiry is a lost {lossWord}.
            </h3>
            <p style={finalPStyle}>
              Don't let your static website cost you another {isSchool ? 'student' : audienceWord}. We've built the engine — you just need the keys.
            </p>
          </div>
          <div style={finalQrCardStyle}>
            <div style={qrLabelStyle}>Talk to sales · WhatsApp</div>
            <h3 style={qrTitleStyle}>Scan to book your demo</h3>
            <div style={qrImgStyle}>
              <img src="/qr-code8555811800.png" alt="Scan to chat with Serviti AI sales on WhatsApp" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" style={qrCtaStyle}>
              💬 Open WhatsApp Chat
            </a>
            <div style={qrOrStyle}>or</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <a href={`tel:${SALES_PHONE}`} style={qrAltStyle}>📞 Call</a>
              <a href={`mailto:${SALES_EMAIL}?subject=Demo`} style={qrAltStyle}>✉ Email</a>
            </div>
          </div>
        </section>

        <footer style={footerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/serviti_logo_dark.png" alt="Serviti AI" style={{ height: 20, opacity: 0.6 }} />
            <span>© Serviti AI {new Date().getFullYear()}</span>
          </div>
          <div style={{ display: 'flex', gap: 20 }}>
            <a href="https://serviti.ai" style={footerLinkStyle}>serviti.ai</a>
            <a href={`mailto:${SALES_EMAIL}`} style={footerLinkStyle}>{SALES_EMAIL}</a>
            <a href={`tel:${SALES_PHONE}`} style={footerLinkStyle}>{SALES_PHONE_PRETTY}</a>
          </div>
        </footer>
      </main>

      {/* Sales-only Tweaks panel — appears with ?edit=1, lets sales personalize URL params live.
          Drop ?edit=1 from the URL bar before sharing the link with prospects. */}
      <TweaksPanel open={isEdit} onClose={() => clearParam('edit')} title="Personalize Demo">
        <TweakSection label="Tenant">
          <TweakText
            label="School name"
            value={searchParams.get('school') || ''}
            placeholder="e.g. Greenwood International School"
            onChange={(v) => setParam('school', v)}
          />
          <TweakText
            label="Business name"
            value={searchParams.get('business') || ''}
            placeholder="e.g. Acme Cafe"
            onChange={(v) => setParam('business', v)}
          />
          <TweakText
            label="Generic name"
            value={searchParams.get('name') || ''}
            placeholder="(used if school + business empty)"
            onChange={(v) => setParam('name', v)}
          />
        </TweakSection>
        <TweakSection label="Vertical">
          <TweakRadio
            label="Type"
            value={searchParams.get('type') || (searchParams.get('school') ? 'school' : 'business')}
            options={[
              { value: 'school', label: 'School' },
              { value: 'business', label: 'Business' },
            ]}
            onChange={(v) => setParam('type', v)}
          />
        </TweakSection>
        <TweakSection label="Share">
          <div style={{ fontSize: 11, color: 'rgba(41,38,27,.6)', lineHeight: 1.4 }}>
            Copy the URL from the address bar (drop <code style={{ background: 'rgba(0,0,0,.06)', padding: '0 4px', borderRadius: 3 }}>&amp;edit=1</code>), paste into WhatsApp / email.
          </div>
        </TweakSection>
      </TweaksPanel>
    </div>
  );
};

// ============================================================
// SUB-COMPONENTS
// ============================================================

function Section({ eyebrow, title, sub, children }) {
  return (
    <section style={{ marginTop: 48 }}>
      <div style={sectionEyebrowStyle}>{eyebrow}</div>
      <h2 style={sectionTitleStyle}>{title}</h2>
      {sub && <p style={sectionSubStyle}>{sub}</p>}
      <div style={{ marginTop: 24 }}>{children}</div>
    </section>
  );
}

function MockChatWidget({ tenantName, shortName, isSchool }) {
  // Auto-playing scripted chat for /preview (synthetic agent — no real backend).
  // Sits where the production widget would and loops automatically.
  const SCRIPT = isSchool ? [
    { who: 'bot', t: `Hello! Welcome to ${shortName}. I can help with programs, fees, eligibility, scholarships, callbacks, and campus visits.` },
    { who: 'user', t: 'Can I schedule a campus visit?' },
    { who: 'bot', t: 'Absolutely. Please share your name, phone number, preferred day and time, branch, and the grade or program you are interested in.' },
    { who: 'user', t: 'Priya 9845453880 Saturday 12:30 branch Nallagandla interested CBSE 12th' },
    { who: 'bot', t: 'Thanks, Priya. I have your visit request for Saturday 12:30 at Nallagandla, for CBSE 12th. Our admissions team will confirm on WhatsApp shortly.' },
    { who: 'capture', t: 'Lead captured · Admissions team alerted on WhatsApp + Email' },
  ] : [
    { who: 'bot', t: `Hello! Welcome to ${shortName}. I can help with availability, visit scheduling, callbacks, pricing, and project details.` },
    { who: 'user', t: 'Can I schedule a visit?' },
    { who: 'bot', t: 'Absolutely. Please share your name, phone number, preferred day and time, location or branch, and what you are interested in.' },
    { who: 'user', t: 'Priya 9845453880 Saturday 12:30 branch Nallagandla interested 3BHK site visit' },
    { who: 'bot', t: 'Thanks, Priya. I have your visit request for Saturday 12:30 at Nallagandla, for a 3BHK site visit. Our team will confirm on WhatsApp shortly.' },
    { who: 'capture', t: 'Lead captured · Sales team alerted on WhatsApp + Email' },
  ];

  const [step, setStep] = React.useState(0);
  const [typing, setTyping] = React.useState(false);
  const [minimized, setMinimized] = React.useState(false);
  const scrollRef = React.useRef(null);
  const widgetRef = React.useRef(null);
  const launcherRef = React.useRef(null);

  React.useEffect(() => {
    // End of script — pause to let viewer read the "Lead captured" pill, then auto-replay.
    if (step >= SCRIPT.length) {
      const t = setTimeout(() => setStep(0), 6000);
      return () => clearTimeout(t);
    }
    const next = SCRIPT[step];
    const delay = step === 0 ? 1200 : (next.who === 'user' ? 1800 : 3000);
    if (next.who === 'bot') {
      const t1 = setTimeout(() => setTyping(true), 500);
      const t2 = setTimeout(() => { setTyping(false); setStep(s => s + 1); }, delay);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    } else {
      const t = setTimeout(() => setStep(s => s + 1), delay);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [step, typing]);

  React.useEffect(() => {
    if (minimized) return undefined;

    const handlePointerDown = (event) => {
      if (widgetRef.current?.contains(event.target)) return;
      if (launcherRef.current?.contains(event.target)) return;
      setMinimized(true);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [minimized]);

  if (minimized) {
    return (
      <button
        ref={launcherRef}
        className="serviti-mock-launcher"
        type="button"
        onClick={() => setMinimized(false)}
        aria-label="Open chat demo"
        title="Open chat demo"
        style={mockLauncherStyle}
      >
        <span style={mockLauncherAvatarStyle}>{shortName.charAt(0)}</span>
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>AI Assistant</span>
          <span style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>Demo running</span>
        </span>
      </button>
    );
  }

  return (
    <div ref={widgetRef} className="serviti-mock-widget" style={mockWidgetStyle}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', color: '#fff', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <div style={mockHeaderBrandStyle} aria-label="Serviti AI">
          <img src="/serviti_app_icon_192.png" alt="" style={mockHeaderIconStyle} />
          <span style={mockHeaderBrandTextStyle}>Serviti AI</span>
        </div>
        <div style={{ flex: 1, lineHeight: 1.2, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tenantName}</div>
          <div style={{ fontSize: 11, color: '#bfdbfe', display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <span style={{ width: 6, height: 6, background: '#4ade80', borderRadius: '50%' }}></span>
            AI Assistant · Online · Replies in seconds
          </div>
        </div>
        <button onClick={() => setMinimized(true)} title="Minimize chat demo" aria-label="Minimize chat demo"
                style={{ width: 26, height: 26, borderRadius: 6, background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.25)', color: '#fff', cursor: 'pointer', fontSize: 18, lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          _
        </button>
      </div>

      {/* Body */}
      <div ref={scrollRef} style={{ flex: 1, padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, background: '#f8fafc' }}>
        {SCRIPT.slice(0, step).map((m, i) => {
          if (m.who === 'capture') {
            return (
              <div key={i} style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#166534', borderRadius: 10, padding: '10px 12px', fontSize: 12, fontWeight: 600, marginTop: 4 }}>
                ✓ {m.t}
              </div>
            );
          }
          const isBot = m.who === 'bot';
          return (
            <div key={i} style={{
              alignSelf: isBot ? 'flex-start' : 'flex-end',
              background: isBot ? '#fff' : '#2563eb',
              color: isBot ? '#111827' : '#fff',
              border: isBot ? '1px solid #e5e7eb' : 'none',
              padding: '10px 12px',
              borderRadius: 12,
              borderBottomLeftRadius: isBot ? 3 : 12,
              borderBottomRightRadius: isBot ? 12 : 3,
              fontSize: 13.5, lineHeight: 1.45,
              maxWidth: '85%',
            }}>{m.t}</div>
          );
        })}
        {typing && (
          <div style={{ alignSelf: 'flex-start', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, borderBottomLeftRadius: 3, padding: '10px 14px', display: 'flex', gap: 4 }}>
            <span className="serviti-mock-dot"></span>
            <span className="serviti-mock-dot"></span>
            <span className="serviti-mock-dot"></span>
          </div>
        )}
      </div>

      {/* Input bar (visual only) */}
      <div style={{ borderTop: '1px solid #e5e7eb', padding: 10, display: 'flex', gap: 8, background: '#fff' }}>
        <div style={{ flex: 1, background: '#f3f4f6', borderRadius: 9999, padding: '10px 14px', fontSize: 13, color: '#9ca3af' }}>Type a message…</div>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>→</div>
      </div>
    </div>
  );
}

function PhoneMock({ tenantName, shortName, isSchool }) {
  const lines = isSchool ? [
    { who: 'bot', t: `Hi 👋 Ask me anything about admissions — fees, programs, eligibility.` },
    { who: 'user', t: 'Grade 6 fees?' },
    { who: 'bot', t: 'Annual fee is ₹1,42,000. Want to book a campus visit?' },
    { who: 'user', t: 'Yes — call me on 9876543210' },
  ] : [
    { who: 'bot', t: `Hi 👋 I'm ${shortName}'s assistant. How can I help?` },
    { who: 'user', t: 'Pricing for premium?' },
    { who: 'bot', t: '₹4,999/mo. Want a callback?' },
    { who: 'user', t: 'Yes — 9876543210' },
  ];
  return (
    <div style={phoneStyle}>
      <div style={phoneBarStyle}>
        <div style={phoneAvStyle}>{shortName.charAt(0)}</div>
        <div style={{ flex: 1, lineHeight: 1.1 }}>
          <b style={{ display: 'block', fontSize: 11, fontWeight: 600 }}>{tenantName}</b>
          <span style={{ fontSize: 9, color: '#bfdbfe', display: 'flex', alignItems: 'center', gap: 4 }}>
            <i style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }}></i>
            AI assistant · online
          </span>
        </div>
      </div>
      <div style={phoneBodyStyle}>
        {lines.map((l, i) => (
          <div key={i} style={l.who === 'bot' ? phoneBubbleBotStyle : phoneBubbleUserStyle}>{l.t}</div>
        ))}
        <div style={phoneCapturedStyle}>
          ✓ Lead captured · Team alerted
        </div>
      </div>
    </div>
  );
}

function ChannelCard({ color, bg, title, desc, children }) {
  return (
    <div style={channelStyle}>
      <div style={{ ...channelIllStyle, background: bg, color }}>
        {children}
      </div>
      <h4 style={channelHStyle}>{title}</h4>
      <p style={channelPStyle}>{desc}</p>
    </div>
  );
}

function DashStat({ color, num, lbl }) {
  return (
    <div style={dashStatStyle}>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1, color }}>{num}</div>
      <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 6 }}>{lbl}</div>
    </div>
  );
}

function Badge({ kind, children }) {
  const styles = {
    hot:  { bg: '#fee2e2', fg: '#b91c1c', dot: '#ef4444' },
    warm: { bg: '#fef3c7', fg: '#b45309', dot: '#f59e0b' },
    new:  { bg: '#dbeafe', fg: '#1e40af', dot: '#2563eb' },
    qual: { bg: '#dcfce7', fg: '#166534', dot: '#22c55e' },
    cold: { bg: '#f3f4f6', fg: '#4b5563', dot: '#9ca3af' },
  };
  const s = styles[kind] || styles.cold;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 9999, background: s.bg, color: s.fg }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }}></span>
      {children}
    </span>
  );
}

function WhatsAppMock({ tenantName, isSchool }) {
  return (
    <div style={alertCardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ ...alertIconStyle, background: '#dcfce7', color: '#16a34a' }}>💬</div>
        <div>
          <h4 style={alertHStyle}>WhatsApp Alert</h4>
          <p style={alertPStyle}>Sent to your team in real-time</p>
        </div>
      </div>
      <div style={waMockStyle}>
        <span style={waTagStyle}>Serviti AI · Bot</span>
        <div style={waMsgStyle}>
          <b style={{ color: '#075e54' }}>🔔 New Hot Lead — {tenantName}</b><br/>
          <b>{isSchool ? 'Parent' : 'Lead'}:</b> Priya Sharma<br/>
          <b>Phone:</b> +91 98765 4XXXX<br/>
          <b>Asked:</b> {isSchool ? 'Grade 6 fees, CBSE vs IB' : 'Pricing, availability'}<br/>
          <b>Intent:</b> {isSchool ? 'Wants campus visit Saturday' : 'Wants demo call'}<br/>
          <b>Priority:</b> 🔥 Hot<br/>
          <a href="#" style={{ color: '#075e54', fontWeight: 700 }}>Open lead →</a>
          <span style={{ display: 'block', textAlign: 'right', fontSize: 10, color: '#667781', marginTop: 4 }}>11:42 AM ✓✓</span>
        </div>
      </div>
    </div>
  );
}

function EmailMock({ tenantName, shortName, isSchool }) {
  const domain = shortName.toLowerCase().replace(/\s+/g, '');
  return (
    <div style={alertCardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ ...alertIconStyle, background: '#dbeafe', color: '#2563eb' }}>📧</div>
        <div>
          <h4 style={alertHStyle}>Email Alert</h4>
          <p style={alertPStyle}>Detailed conversation summary in your inbox</p>
        </div>
      </div>
      <div style={emMockStyle}>
        <div style={emHeadStyle}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#1d4ed8', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>SA</div>
          <div style={{ flex: 1, lineHeight: 1.2 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: '#111827' }}>Serviti AI &lt;alerts@serviti.ai&gt;</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>to {isSchool ? 'admissions' : 'sales'}@{domain}.com</div>
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>11:42 AM</div>
        </div>
        <div style={{ padding: 14, fontSize: 12.5, lineHeight: 1.55, color: '#1f2937' }}>
          <h5 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 700, color: '#111827' }}>🔔 New qualified lead — Priya Sharma</h5>
          <EmRow label="Phone" value="+91 98765 4XXXX" />
          <EmRow label={isSchool ? 'Grade' : 'Interest'} value={isSchool ? 'Grade 6 (joining 2026-27)' : 'Premium plan'} />
          <EmRow label="Source" value="Website chat widget" />
          <EmRow label="Priority" value={<span style={{ color: '#b91c1c', fontWeight: 600 }}>🔥 Hot</span>} />
          <div style={emSummaryStyle}>
            <b>Conversation summary:</b> {isSchool
              ? 'Parent inquired about Grade 6 admissions, fees, and CBSE vs IB curriculum options. AI explained both streams and fee structure. Parent confirmed interest and booked a campus visit for Saturday 11 AM.'
              : 'Visitor inquired about pricing tiers and availability. AI walked through the premium plan and confirmed callback for a demo session.'}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 8, margin: '4px 0' }}>
      <b style={{ width: 80, color: '#6b7280', fontWeight: 500, flexShrink: 0 }}>{label}</b>
      <span>{value}</span>
    </div>
  );
}

const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

// ============================================================
// STYLES
// ============================================================

const pageStyle = { minHeight: '100vh', background: '#f7f8fa', fontFamily: "'Inter', system-ui, -apple-system, sans-serif", color: '#111827' };
const mainStyle = { maxWidth: 1200, margin: '0 auto', padding: '0 24px 48px 24px' };
const loadingWrapStyle = { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)' };
const spinnerStyle = { width: 56, height: 56, border: '3px solid #e5e7eb', borderTop: '3px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' };
const spinnerKeyframes = `@keyframes spin { to { transform: rotate(360deg); } }`;

const topbarStyle = { maxWidth: 1200, margin: '0 auto', padding: '18px 24px 0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const brandStyle = { display: 'flex', alignItems: 'center', gap: 12 };
const brandByStyle = { fontSize: 12, color: '#6b7280', fontWeight: 500, borderLeft: '1px solid #e5e7eb', paddingLeft: 12, lineHeight: 1.2, display: 'flex', flexDirection: 'column' };
const topbarQuickStyle = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4b5563' };
const topbarLinkStyle = { color: '#1d4ed8', fontWeight: 600, textDecoration: 'none', padding: '6px 10px', borderRadius: 6 };

const heroStyle = {
  marginTop: 18,
  background: `radial-gradient(800px 420px at 85% -10%, rgba(96,165,250,.35), transparent 60%), radial-gradient(700px 500px at -10% 110%, rgba(29,78,216,.55), transparent 60%), linear-gradient(135deg, #0f1e3d 0%, #1d4ed8 55%, #2563eb 100%)`,
  color: '#fff', borderRadius: 24, padding: '36px', overflow: 'hidden', position: 'relative',
};

const heroContentStyle = { maxWidth: 720 };
const heroPillStyle = { display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.22)', color: '#fff', fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 9999 };
const heroPillDotStyle = { width: 7, height: 7, background: '#4ade80', borderRadius: '50%', boxShadow: '0 0 0 4px rgba(74,222,128,.25)' };
const heroEyebrowStyle = { marginTop: 18, fontSize: 12, fontWeight: 700, color: '#bfdbfe', letterSpacing: '0.18em', textTransform: 'uppercase' };
const heroTitleStyle = { margin: '8px 0 0 0', fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.025em', color: '#fff' };
const heroSubStyle = { margin: '14px 0 0 0', fontSize: 16, lineHeight: 1.5, color: '#dbeafe', fontWeight: 400, maxWidth: 560 };
const heroMetricStyle = { background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.16)', borderRadius: 12, padding: '12px 14px' };
const heroMetricNumStyle = { fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', color: '#fff', lineHeight: 1 };
const heroMetricLblStyle = { fontSize: 11, color: '#bfdbfe', marginTop: 4, letterSpacing: '0.02em' };

const finalQrCardStyle = { background: '#fff', borderRadius: 16, padding: 20, color: '#111827', boxShadow: '0 25px 50px -12px rgba(0,0,0,.28)', display: 'flex', flexDirection: 'column', alignSelf: 'center', width: '100%', maxWidth: 340, justifySelf: 'end' };
const qrLabelStyle = { fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', color: '#1d4ed8', textTransform: 'uppercase' };
const qrTitleStyle = { margin: '6px 0 10px 0', fontSize: 18, fontWeight: 700, color: '#111827', lineHeight: 1.25, letterSpacing: '-0.01em' };
const qrImgStyle = { width: '100%', aspectRatio: '1', maxWidth: 260, margin: '6px auto 12px auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const qrCtaStyle = { display: 'block', textAlign: 'center', background: '#25d366', color: '#fff', fontWeight: 700, fontSize: 14, padding: 12, borderRadius: 10, textDecoration: 'none' };
const qrOrStyle = { textAlign: 'center', fontSize: 12, color: '#6b7280', margin: '10px 0 8px 0' };
const qrAltStyle = { flex: 1, textAlign: 'center', border: '1px solid #e5e7eb', color: '#1f2937', fontWeight: 600, fontSize: 13, padding: '9px 8px', borderRadius: 8, textDecoration: 'none' };

const sectionEyebrowStyle = { fontSize: 12, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#1d4ed8', marginBottom: 8 };
const sectionTitleStyle = { fontSize: 28, fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.02em', color: '#0f172a', margin: '0 0 8px 0' };
const sectionSubStyle = { fontSize: 16, color: '#4b5563', lineHeight: 1.55, maxWidth: 720, margin: 0 };

const stepStyle = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 20 };
const stepNumStyle = { width: 28, height: 28, background: '#1d4ed8', color: '#fff', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, marginBottom: 12 };
const stepHStyle = { fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 6px 0', lineHeight: 1.3 };
const stepPStyle = { fontSize: 13.5, color: '#4b5563', margin: 0, lineHeight: 1.5 };

const channelStyle = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 18, textAlign: 'center' };
const channelIllStyle = { height: 56, width: 56, margin: '0 auto 10px auto', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const channelHStyle = { fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 4px 0' };
const channelPStyle = { fontSize: 12, lineHeight: 1.4, color: '#6b7280', margin: 0 };

const flowRowStyle = { marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontSize: 13, color: '#374151', padding: '12px 16px', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 12, flexWrap: 'wrap' };
const flowArrowStyle = { color: '#a855f7', fontWeight: 700 };

const dashCardStyle = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,.04)' };
const dashHeadStyle = { background: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: '#6b7280', fontWeight: 500 };
const dashDotStyle = { width: 10, height: 10, borderRadius: '50%', background: '#d1d5db', display: 'inline-block' };
const dashUrlStyle = { marginLeft: 8, background: '#fff', border: '1px solid #e5e7eb', padding: '4px 12px', borderRadius: 6, fontSize: 12, color: '#4b5563', fontFamily: 'SF Mono, Menlo, monospace' };
const dashLiveStyle = { marginLeft: 'auto', color: '#16a34a', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 };
const dashLiveDotStyle = { width: 7, height: 7, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 3px rgba(34,197,94,.2)' };
const dashStatStyle = { padding: '18px 20px', borderRight: '1px solid rgba(255,255,255,.08)' };

const leadsTableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const leadsThStyle = { background: '#f9fafb', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 18px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' };
const leadsTdStyle = { padding: '14px 18px', verticalAlign: 'middle' };

const alertCardStyle = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 20 };
const alertIconStyle = { width: 36, height: 36, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 };
const alertHStyle = { fontSize: 15, fontWeight: 700, margin: 0, color: '#111827' };
const alertPStyle = { fontSize: 12.5, color: '#6b7280', margin: '2px 0 0 0' };

const waMockStyle = { background: '#ece5dd', borderRadius: 12, padding: 12, minHeight: 200 };
const waTagStyle = { display: 'inline-block', background: 'rgba(255,255,255,.85)', color: '#075e54', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, marginBottom: 6 };
const waMsgStyle = { background: '#d9fdd3', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.5, color: '#111827', maxWidth: '92%', boxShadow: '0 1px 1px rgba(0,0,0,.06)' };

const emMockStyle = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' };
const emHeadStyle = { background: '#f9fafb', padding: '10px 14px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10 };
const emSummaryStyle = { marginTop: 10, padding: 10, background: '#f9fafb', borderLeft: '3px solid #1d4ed8', borderRadius: 4, color: '#374151' };

const setupStyle = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 28, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 };
const setupLiStyle = { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: '#374151', lineHeight: 1.5 };
const timingStyle = { background: 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)', border: '1px solid #bfdbfe', borderRadius: 12, padding: 18, display: 'flex', flexDirection: 'column', justifyContent: 'center' };
const timingBigStyle = { fontSize: 36, fontWeight: 800, color: '#1d4ed8', letterSpacing: '-0.02em', lineHeight: 1 };
const timingLblStyle = { fontSize: 13, color: '#4b5563', marginTop: 6 };
const timingNoteStyle = { marginTop: 12, fontSize: 12.5, color: '#6b7280', lineHeight: 1.5 };

const finalStyle = { marginTop: 48, background: 'linear-gradient(135deg, #0f1e3d 0%, #1d4ed8 100%)', borderRadius: 20, padding: 36, color: '#fff', display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 28, alignItems: 'center', overflow: 'hidden', position: 'relative' };
const finalTitleStyle = { fontSize: 'clamp(22px, 3vw, 32px)', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, margin: '0 0 10px 0', color: '#fff' };
const finalPStyle = { fontSize: 15, color: '#dbeafe', margin: 0, lineHeight: 1.55 };

const footerStyle = { marginTop: 48, padding: '24px 0', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12.5, color: '#6b7280', flexWrap: 'wrap', gap: 12 };
const footerLinkStyle = { color: '#4b5563', textDecoration: 'none' };

// Phone mock (from flyer)
const phoneStyle = { background: '#fff', borderRadius: 16, boxShadow: '0 25px 50px -12px rgba(0,0,0,.4)', overflow: 'hidden', maxWidth: 280, margin: '24px auto 0', transform: 'rotate(-1deg)' };

// MockChatWidget — floating, fixed bottom-right (where production widget would be)
const mockWidgetStyle = {
  position: 'fixed', right: 24, bottom: 24, zIndex: 40,
  width: 420, height: 600, maxWidth: 'calc(100vw - 48px)', maxHeight: 'calc(100vh - 48px)',
  background: '#fff', borderRadius: 16, overflow: 'hidden',
  boxShadow: '0 25px 50px -12px rgba(0,0,0,.4), 0 10px 20px -5px rgba(0,0,0,.15)',
  display: 'flex', flexDirection: 'column',
};
const mockHeaderBrandStyle = {
  width: 44,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
  flexShrink: 0,
};
const mockHeaderIconStyle = {
  width: 30,
  height: 30,
  borderRadius: 8,
  background: '#fff',
  padding: 4,
  objectFit: 'contain',
  boxSizing: 'border-box',
};
const mockHeaderBrandTextStyle = {
  fontSize: 8,
  fontWeight: 700,
  lineHeight: 1,
  color: 'rgba(255,255,255,.92)',
  whiteSpace: 'nowrap',
};
const mockLauncherStyle = {
  position: 'fixed', right: 24, bottom: 24, zIndex: 40,
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '10px 14px 10px 10px',
  background: '#fff',
  border: '1px solid #dbeafe',
  borderRadius: 9999,
  boxShadow: '0 14px 30px -10px rgba(15,23,42,.35)',
  cursor: 'pointer',
  maxWidth: 'calc(100vw - 48px)',
};
const mockLauncherAvatarStyle = {
  width: 38, height: 38, borderRadius: '50%',
  background: 'linear-gradient(135deg,#2563eb,#1d4ed8)',
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  fontWeight: 800,
  flexShrink: 0,
};
const phoneBarStyle = { background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, color: '#fff' };
const phoneAvStyle = { width: 26, height: 26, borderRadius: '50%', background: '#fff', color: '#1d4ed8', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 };
const phoneBodyStyle = { padding: '10px 10px 8px', display: 'flex', flexDirection: 'column', gap: 6, background: '#f8fafc' };
const phoneBubbleBotStyle = { fontSize: 12, lineHeight: 1.35, padding: '6px 10px', borderRadius: 10, maxWidth: '90%', background: '#fff', border: '1px solid #e5e7eb', color: '#1f2937', borderBottomLeftRadius: 3 };
const phoneBubbleUserStyle = { fontSize: 12, lineHeight: 1.35, padding: '6px 10px', borderRadius: 10, maxWidth: '90%', background: '#2563eb', color: '#fff', alignSelf: 'flex-end', borderBottomRightRadius: 3 };
const phoneCapturedStyle = { background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#166534', fontSize: 11, fontWeight: 600, padding: '5px 8px', borderRadius: 6, alignSelf: 'stretch', textAlign: 'center' };

// Global responsive CSS — JSX inline styles can't do media queries, so we inject this once.
const globalStyle = `
  .serviti-hero-metrics { margin-top: 22px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; max-width: 540px; }
  .serviti-how { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
  .serviti-channels { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
  .serviti-alerts { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .serviti-dash-stats { display: grid; grid-template-columns: repeat(4, 1fr); background: #0f172a; color: #fff; }
  .serviti-phone-mock { display: none; }

  /* Mock chat widget — typing dots + mobile-hide */
  .serviti-mock-dot { width:6px; height:6px; background:#9ca3af; border-radius:50%; animation: serviti-mock-bounce 1.2s infinite ease-in-out; }
  .serviti-mock-dot:nth-child(2) { animation-delay: .15s; }
  .serviti-mock-dot:nth-child(3) { animation-delay: .3s; }
  @keyframes serviti-mock-bounce { 0%,80%,100% { opacity:.3; transform:translateY(0); } 40% { opacity:1; transform:translateY(-3px); } }

  @media (max-width: 1099px) {
    .serviti-phone-mock { display: block; }
    .serviti-mock-widget {
      left: 50% !important;
      right: auto !important;
      bottom: 12px !important;
      width: min(400px, calc(100vw - 20px)) !important;
      height: min(600px, calc(100dvh - 24px)) !important;
      max-width: calc(100vw - 20px) !important;
      max-height: calc(100dvh - 24px) !important;
      transform: translateX(-50%) !important;
    }
    .serviti-mock-launcher {
      left: 50% !important;
      right: auto !important;
      bottom: 12px !important;
      transform: translateX(-50%) !important;
    }
  }
  @media (max-width: 900px) {
    .serviti-how { grid-template-columns: repeat(2, 1fr); }
    .serviti-channels { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 760px) {
    .serviti-alerts { grid-template-columns: 1fr; }
  }
  @media (max-width: 640px) {
    .serviti-dash-stats { grid-template-columns: repeat(2, 1fr); }
    .serviti-how { grid-template-columns: 1fr; }
  }
`;

export default AgentDemoPage;
